import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Switch } from '@travellegacy/nexops-design-system';
import { usersAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { Loader2 } from 'lucide-react';
import type { User } from '@/shared/types';
import { isAxiosError } from 'axios';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
}

const userSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().optional().or(z.literal('')),
  role: z.enum(['ADMIN', 'GESTOR', 'COLLABORATOR', 'CLIENT']),
  notionPageId: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
});

type UserFormData = z.infer<typeof userSchema>;

export function UserModal({ isOpen, onClose, user }: UserModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!user;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    control,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: 'GESTOR',
      notionPageId: '',
      isActive: true,
    },
  });

  const watchRole = watch('role');

  // Preencher form quando estiver editando
  useEffect(() => {
    if (user) {
      setValue('name', user.name);
      setValue('email', user.email);
      setValue('role', user.role as 'ADMIN' | 'GESTOR' | 'COLLABORATOR' | 'CLIENT');
      setValue('password', '');
      setValue('notionPageId', user.notionPageId || '');
      setValue('isActive', user.isActive);
    } else {
      reset();
    }
  }, [user, setValue, reset]);

  // Mutation para criar usuário
  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      return usersAPI.create({
        name: data.name,
        email: data.email,
        password: data.password!,
        role: data.role,
        notionPageId: data.notionPageId || undefined,
        isActive: data.isActive,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Sucesso', 'Usuário criado com sucesso!');
      reset();
      onClose();
    },
    onError: (error) => {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao criar usuário'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    },
  });

  // Mutation para atualizar usuário
  const updateMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const updateData: {
        name: string;
        email: string;
        role: 'ADMIN' | 'GESTOR' | 'COLLABORATOR' | 'CLIENT';
        isActive: boolean;
        notionPageId?: string;
        password?: string;
      } = {
        name: data.name,
        email: data.email,
        role: data.role,
        isActive: data.isActive,
        notionPageId: data.notionPageId || undefined,
      };

      // Só incluir senha se foi preenchida
      if (data.password && data.password.length > 0) {
        updateData.password = data.password;
      }

      return usersAPI.update(user!.id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Sucesso', 'Usuário atualizado com sucesso!');
      reset();
      onClose();
    },
    onError: (error) => {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao atualizar usuário'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    },
  });

  const onSubmit = (data: UserFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      // Validar senha obrigatória na criação
      if (!data.password || data.password.length === 0) {
        toast.error('Erro', 'Senha é obrigatória ao criar um usuário');
        return;
      }
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogClose onClick={onClose} />
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-4">
            {/* Active Status */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">Status</p>
                <p className="text-sm text-muted-foreground">
                  Usuário {watch('isActive') ? 'ativo' : 'inativo'}
                </p>
              </div>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>

            <Input
              label="Nome"
              placeholder="Nome completo"
              {...register('name')}
              error={errors.name?.message}
            />

            <Input
              label="Email"
              type="email"
              placeholder="email@exemplo.com"
              {...register('email')}
              error={errors.email?.message}
            />

            <Input
              label={isEditing ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
              type="password"
              placeholder={isEditing ? 'Digite para alterar' : '••••••••'}
              {...register('password')}
              error={errors.password?.message}
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">Função</label>
              <select
                {...register('role')}
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="GESTOR">Gestor</option>
                <option value="ADMIN">Administrador</option>
                <option value="COLLABORATOR">Colaborador</option>
                <option value="CLIENT">Cliente</option>
              </select>
              {errors.role && <p className="text-sm text-red-500">{errors.role.message}</p>}
            </div>

            {watchRole === 'CLIENT' && (
              <Input
                label="Notion Page ID"
                placeholder="ID da página do Notion"
                {...register('notionPageId')}
                error={errors.notionPageId?.message}
              />
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
